import {
  applyActionCode,
  connectAuthEmulator,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  verifyPasswordResetCode,
  getAuth,
  type Auth,
} from "firebase/auth";
import { app } from "./app";
import { AuthRole } from "@/types/v1";
import { AuthCustomClaims } from "@/types/v2";
import { httpsCallables } from "@/repositories/httpsCallables";
import { config } from "@/config";

let auth: Auth;
try {
  auth = getAuth(app);
  if (config.emulator) {
    connectAuthEmulator(auth, "http://localhost:9099");
  }
} catch {
  // getAuth() throws synchronously when Firebase config is missing/invalid
  // (e.g. no VITE_FIREBASE_* env vars) -- keep module import safe in
  // contexts without a real Firebase config (isolated bundling/tooling)
  // instead of crashing every consumer of this module.
  auth = {} as Auth;
}

const authActions = {
  currentUser: () => {
    return auth.currentUser;
  },
  getUserClaims: async (shopId?: string) => {
    const user = auth.currentUser;
    if (user) {
      let claims = await user.getIdTokenResult(true);
      // Migrate for v3.1.0
      if (
        !(claims.claims as AuthCustomClaims).user &&
        !(claims.claims as AuthCustomClaims).superAdmin
      ) {
        // Migrate User claims
        await httpsCallables.updateUserClaims({
          userId: user.uid,
          userShopId: shopId,
        });
        await user.getIdToken(true);
        claims = await user.getIdTokenResult();
      }
      if (
        shopId &&
        !(claims.claims as AuthCustomClaims).superAdmin &&
        claims.claims.shops &&
        !claims.claims.shops[shopId as keyof AuthCustomClaims["shops"]]
      ) {
        // Migrate User claims
        await httpsCallables.updateUserClaims({
          userId: user.uid,
          userShopId: shopId,
        });
        await user.getIdToken(true);
        claims = await user.getIdTokenResult();
      }
      return claims.claims as AuthCustomClaims;
    }
    return;
  },
  getUserClaimRole: async () => {
    const claims = await authActions.getUserClaims();
    if (claims?.superAdmin) {
      return "superAdmin" as AuthRole;
    }
    if (claims) {
      return "user" as AuthRole;
    }
    return;
  },
  createUserWithEmailAndPassword: async (email: string, password: string) => {
    return createUserWithEmailAndPassword(auth, email, password);
  },
  verifyEmail: async (oobCode: string) => {
    return applyActionCode(auth, oobCode);
  },
  verifyPasswordResetCode: async (oobCode: string) => {
    return verifyPasswordResetCode(auth, oobCode);
  },
  confirmPasswordReset: async (oobCode: string, newPassword: string) => {
    return confirmPasswordReset(auth, oobCode, newPassword);
  },
};

export { auth, authActions };
